import {
  RADAR_LABEL_ARRAY,
  SINGLE_POPULATION_RADAR_CHART_DATASET,
} from "app/storybookComponents/Charts/constants";
import { WorkplaceInsightsReport, StrengthTip } from "./types";
import { Card } from "react-bootstrap";
import {
  LIKERT_SCALE_VALUES,
  WORKPLACEINSIGHTS_COACHBOT_PROMPT,
  WORK_PLACE_INSIGHTS_REPORT_SECTION_TITLES,
} from "./constants";
import {
  getTraitNameAndIcon,
  getWorkplaceChallengesAndMotivatorsCell,
} from "./helper-functions";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectCurrentUserAccountId,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";
import NotAvailable from "./NotAvailable";
import PendingTest from "./PendingTest";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getSuperpowersAndHiddenStrengths,
  selectSuperpowersAndHiddenStrengthsById,
  getPendingTests,
  unlinkTestTaker,
  selectNewEventId,
} from "./slice";
import SimpleCollapsibleCard from "app/storybookComponents/CollapsibleCards/SimpleCollapsibleCard";
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import RadarChart from "app/storybookComponents/Charts/RadarChart";
import SampleBanner from "../Team360Assessment/SampleBanner";
import { selectCompanySettings } from "app/containers/AdminConsole/slice";
import {
  resetChatbotState,
  getIntroMessageAndPreselectedPrompts,
  setCoachBotPrompt,
  setIsCoachbotOpen,
} from "app/containers/Chatbot/slice";
import CustomChatbotButton from "app/containers/Chatbot/CustomChatbotButton";
import Button from "app/storybookComponents/Button";
import RetakeEppModal from "../Modals/RetakeEPPModal";
import { REACT_APP_ODA_URL } from "utils/environmentVariables";
import { LinkResultsModal } from "../LinkResultsModal/LinkResultsModal";
import { useIsMobile } from "utils/hooks";
import usePdfExport from "./usePdfExport";
import PdfDownloadOverlay from "./PdfDownloadOverlay";
import AvatarCircle from "../AvatarCircle";
import { getUserGuide } from "app/containers/UserGuide/slice";

interface Props {
  workplaceInsightsReport?: WorkplaceInsightsReport;
  userAccountId: number;
  firstName: string;
  pendingTestId?: number | string | null;
}

export default function WorkPlaceInsightsReport({
  workplaceInsightsReport,
  userAccountId,
  firstName,
  pendingTestId,
}: Readonly<Props>) {
  const dispatch = useAppDispatch();
  // Sections that should always start on a new PDF page, in the order they
  // appear in the DOM. Add more refs here to force a break before them.
  const personalitySummaryRef = useRef<HTMLDivElement>(null);
  const moreInsightsRef = useRef<HTMLDivElement>(null);
  const pageBreakRefs = [personalitySummaryRef, moreInsightsRef];
  // ----------------------- Selectors ----------------------- //
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const superpowerAndHiddenStrengths = useAppSelector(
    selectSuperpowersAndHiddenStrengthsById(userAccountId)
  );
  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
  const companyUsersById = useAppSelector(selectAllCompanyUsersById);
  const companySettings = useAppSelector(selectCompanySettings);
  const isSampleUser = sampleUsersById[userAccountId];
  const newEventId = useAppSelector(selectNewEventId);
  const isMobile = useIsMobile();

  // ----------------------- State ----------------------- //
  const [openedCards, setOpenedCards] = useState<{
    [key: string]: boolean;
  }>({
    SuperpowerSection: true, // Default to open
    HiddenStrengthSection: true, // Default to open
    SecondHighestTraitSection: true,
  });
  const [moreInsightsShowing, setMoreInsightsShowing] =
    useState("notable-traits");
  const [showRetakeEppModal, setShowRetakeEppModal] = useState(false);
  const [navigatedToAssessment, setNavigatedToAssessment] = useState(false);
  const [showLinkResultsModal, setShowLinkResultsModal] = useState(false);
  const { targetRef, isPDFBeingDownloaded, pageBreakSpacers, downloadPDF } =
    usePdfExport({
      pageBreakRefs,
      filename: `${firstName}'s Workplace Insights Report.pdf`,
      // Pre-fetch the user guide so AvatarCircle has a base64 profile picture
      // (html2canvas can't load cross-origin image URLs directly). Skip it
      // if there's no profile picture set - initials will render either way.
      beforeCapture: () => {
        const hasProfilePicture =
          companyUsersById[userAccountId]?.profilePicture ||
          sampleUsersById[userAccountId]?.profilePicture;
        return hasProfilePicture
          ? dispatch(getUserGuide(userAccountId))
          : undefined;
      },
    });

  // ----------------------- Effects ----------------------- //
  useEffect(() => {
    if (superpowerAndHiddenStrengths) return;
    dispatch(getSuperpowersAndHiddenStrengths(userAccountId));
  }, [userAccountId, dispatch, superpowerAndHiddenStrengths]);

  useEffect(() => {
    if (newEventId && !navigatedToAssessment) {
      window.open(`${REACT_APP_ODA_URL}verify/index/${newEventId}`);
      setNavigatedToAssessment(true);
    }
  }, [newEventId, navigatedToAssessment]);

  // ----------------------- Memos ----------------------- //
  const radarChart = useMemo(() => {
    const data = workplaceInsightsReport?.traitScores
      ? RADAR_LABEL_ARRAY.map(
          (label) => workplaceInsightsReport.traitScores[label] + 25
        )
      : [];
    const dataSet = {
      ...SINGLE_POPULATION_RADAR_CHART_DATASET,
      data,
      label: firstName,
    };

    return (
      <RadarChart
        canvasId={`${userAccountId}-workplace-report`}
        dataSets={[dataSet]}
        includeDashedBaseline
        includeGeneralPopulation
      />
    );
  }, [workplaceInsightsReport?.traitScores, userAccountId, firstName]);

  // ----------------------- Functions ----------------------- //
  const getNotableTraits = () =>
    workplaceInsightsReport?.notableTraits?.map(({ title, text, color }) => (
      <Card
        key={title}
        className="right-bar-card p-3"
        style={{ borderLeftColor: color }}
      >
        <h3>{title}</h3>
        <p>{text}</p>
      </Card>
    ));

  const getWorkStyle = () => {
    if (!workplaceInsightsReport?.workStyle) {
      return null;
    }
    const col1: ReactElement[] = [];
    const col2: ReactElement[] = [];

    Object.entries(workplaceInsightsReport.workStyle)?.forEach(
      ([abbrev, { title, text }], idx) => {
        const traitIcon = getTraitNameAndIcon(abbrev)?.traitIcon;
        const item = (
          <div key={abbrev}>
            <div className="title-with-icon">
              {traitIcon ? (
                <img
                  src={traitIcon}
                  alt="title icon"
                  height="32px"
                  width="32px"
                  className="me-2"
                />
              ) : null}
              <h3>{title}</h3>
            </div>
            <p>{text}</p>
          </div>
        );
        if (idx % 2 === 0) {
          col1.push(item);
        } else {
          col2.push(item);
        }
      }
    );

    return (
      <>
        <div className="column-gap-12px">{col1}</div>
        <div className="column-gap-12px">{col2}</div>
      </>
    );
  };

  const getTemperamentAttitudeOutlook = () => {
    if (!workplaceInsightsReport?.temperament) {
      return null;
    }
    return Object.entries(workplaceInsightsReport.temperament)?.map(
      ([abbrev, { title, text }]) => {
        const traitIcon = getTraitNameAndIcon(abbrev)?.traitIcon;
        return (
          <div key={abbrev}>
            <div className="title-with-icon">
              {traitIcon ? (
                <img
                  src={traitIcon}
                  alt="title icon"
                  height="32px"
                  width="32px"
                  className="me-2"
                />
              ) : null}
              <h3>{title}</h3>
            </div>
            <p>{text}</p>
          </div>
        );
      }
    );
  };

  const getStrengthAndPotentialChallenges = () => {
    const { strengths, weaknesses } = workplaceInsightsReport || {};
    const strengthElms = strengths?.map((strength) => (
      <li key={strength}>{strength}</li>
    ));
    const weaknessElms = weaknesses?.map((weakness) => (
      <li key={weakness}>{weakness}</li>
    ));

    return (
      <>
        <div className="column-gap-12px">
          <h3 className="strength-and-challenges-title">Strengths</h3>
          <ul>{strengthElms}</ul>
        </div>
        <div className="column-gap-12px">
          <h3 className="strength-and-challenges-title">
            Potential Challenges
          </h3>
          <ul>{weaknessElms}</ul>
        </div>
      </>
    );
  };

  const addHrsInBetweenListItems = (list: any[]) => {
    const newList: any[] = [];
    list.forEach((item, idx) => {
      newList.push(item);
      if (idx !== list.length - 1) {
        newList.push(<hr key={idx} className="m-0" />);
      }
    });
    return newList;
  };

  const getDevelopmentSuggestions = () => {
    const col1: ReactElement[] = [];
    const col2: ReactElement[] = [];

    workplaceInsightsReport?.suggestions?.forEach(
      ({ title, suggestions, color }, idx) => {
        const elm = (
          <div key={title} className="column-gap-8px">
            <h3
              className="development-suggestions-title-badge"
              style={{ backgroundColor: color }}
            >
              {title}
            </h3>
            <div style={{ paddingTop: "5px" }}>
              {suggestions.map((sug) => (
                <p key={sug}>{sug}</p>
              ))}
            </div>
          </div>
        );
        if (idx % 2 === 0) {
          col1.push(elm);
        } else {
          col2.push(elm);
        }
      }
    );
    return (
      <>
        <div className="column-gap-20px">{addHrsInBetweenListItems(col1)}</div>
        <div className="column-gap-20px">{addHrsInBetweenListItems(col2)}</div>
      </>
    );
  };

  const getWorkplaceChallengesAndMotivators = () => (
    <div>
      <p>{workplaceInsightsReport?.workplaceStressorsMotivatorsDescription}</p>
      <div className="workplace-challenges-and-motivators-table-container">
        <table className="workplace-challenges-and-motivators-table">
          <thead>
            <tr>
              <th />
              <th className="text-center">
                Less
                <br />
                comfortable
              </th>
              <th />
              <th className="text-center">
                Somewhat
                <br />
                comfortable
              </th>
              <th />
              <th className="text-center">
                Extremely
                <br />
                comfortable
              </th>
            </tr>
          </thead>
          <tbody>
            {workplaceInsightsReport?.workplaceStressorsMotivators?.map(
              ({ title, likertValue }, idx) => (
                <tr key={title} className={idx % 2 === 0 ? "dark-row" : ""}>
                  <td>{title}</td>
                  {LIKERT_SCALE_VALUES.map((value) =>
                    getWorkplaceChallengesAndMotivatorsCell(
                      value - 1,
                      likertValue,
                      title
                    )
                  )}
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const getOpenedCardsProperty = (title: string) => {
    switch (title) {
      case "Superpower":
        return "SuperpowerSection";
      case "Second Highest Trait":
        return "SecondHighestTraitSection";
      case "Hidden Strengths":
      default:
        return "HiddenStrengthSection";
    }
  };

  const getStrengthsOfThisTitle = (title: string) => {
    switch (title) {
      case "Second Highest Trait":
        return "Strengths Of This Trait";
      case "Superpower":
        return "Strengths Of This Superpower";
      case "Hidden Strengths":
      default:
        return "Hidden Strengths";
    }
  };

  const getRisksOfThisTitle = (title: string) => {
    switch (title) {
      case "Superpower":
        return "Risks Of This Superpower";
      case "Second Highest Trait":
        return "Risks Of This Trait";
      case "Hidden Strengths":
      default:
        return "Hidden Risks";
    }
  };

  const getStrengthSection = (strengthTip: StrengthTip, title: string) => {
    if (!strengthTip) {
      return null;
    }
    const overviewCardDescription =
      currentUserAccountId === userAccountId
        ? strengthTip.summary
        : strengthTip.summaryOfSomeoneElse;

    const openedCardsProperty = getOpenedCardsProperty(title);
    return (
      <>
        <SimpleCollapsibleCard
          title={
            currentUserAccountId === userAccountId
              ? `Your ${title}`
              : `${firstName}'s ${title}`
          }
          setIsOpen={(value: boolean) => {
            setOpenedCards({
              ...openedCards,
              [openedCardsProperty]: value,
            });
          }}
          isOpen={!!openedCards[openedCardsProperty] || isPDFBeingDownloaded}
        >
          <div className="workplace-insights-strengths">
            {getStrengthOverviewCard(
              strengthTip.trait,
              overviewCardDescription
            )}
            <div className="workplace-insights-strengths__lists">
              {getStrengthsList(
                getStrengthsOfThisTitle(title),
                true,
                strengthTip.strengths
              )}
              {getStrengthsList(
                getRisksOfThisTitle(title),
                false,
                strengthTip.risks
              )}
            </div>
          </div>
        </SimpleCollapsibleCard>
      </>
    );
  };

  const getStrengthsList = (
    title?: string,
    strength?: boolean,
    list: string[] = []
  ) => (
    <Card className="column-gap-20px">
      <h3>{title}</h3>
      <ul>
        {list?.map((item) => (
          <li key={item}>
            <FontAwesomeIcon
              icon={strength ? "check" : "xmark"}
              className="mt-1"
              style={{
                color: strength ? "#009952" : "#D50057",
              }}
            />
            <p>{item}</p>
          </li>
        ))}
      </ul>
    </Card>
  );

  const getStrengthOverviewCard = (
    title: string = "",
    description: string = ""
  ) => (
    <Card className="card-with-left-strip dark-blue column-gap-4px workplace-insights-simple-card-with-side-strip">
      <h3>{title}</h3>
      <p>{description}</p>
    </Card>
  );

  const getMoreInsightsNavBox = () => {
    const navItems = Object.entries(
      WORK_PLACE_INSIGHTS_REPORT_SECTION_TITLES
    ).map(([id, title]) => (
      <div
        key={id}
        onClick={() => setMoreInsightsShowing(id)}
        className={moreInsightsShowing === id ? "selected" : ""}
      >
        <span>{title}</span>
      </div>
    ));
    return <div className="navigation-box">{navItems}</div>;
  };

  const getMoreInsightsShowing = (keyOverride?: string) => {
    switch (keyOverride ?? moreInsightsShowing) {
      case "notable-traits":
        return (
          <div
            id="notable-traits"
            className="column-gap-12px workplace-insights-more-insights-card"
          >
            <h2 className="mb-0">Notable Traits</h2>
            <p className="text-muted">
              <em>
                {currentUserAccountId === userAccountId ? "You" : firstName} can
                be described as:
              </em>
            </p>
            <div className="workplace-insights-more-insights-card__section">
              {getNotableTraits()}
            </div>
          </div>
        );
      case "work-communication-interaction-style":
        return (
          <div id="work-communication-interaction-style">
            <h2>Work, Communication & Interaction Style</h2>
            <div className="workplace-insights-more-insights-card__section">
              {getWorkStyle()}
            </div>
          </div>
        );
      case "temperament-attitudes-outlook":
        return (
          <div id="temperament-attitudes-outlook">
            <h2>Temperament, Attitudes & Outlook</h2>
            <div className="workplace-insights-more-insights-card__section">
              {getTemperamentAttitudeOutlook()}
            </div>
          </div>
        );
      case "strengths-potential-challenges":
        return (
          <div id="strengths-potential-challenges">
            <h2>Strengths & Potential Challenges</h2>
            <div className="workplace-insights-more-insights-card__section">
              {getStrengthAndPotentialChallenges()}
            </div>
          </div>
        );
      case "development-suggestions":
        return (
          <div id="development-suggestions">
            <h2>Development Suggestions</h2>
            <div className="workplace-insights-more-insights-card__section">
              {getDevelopmentSuggestions()}
            </div>
          </div>
        );
      case "workplace-challenges-motivators":
        return (
          <div id="workplace-challenges-motivators">
            <h2 className="mb-0">Workplace Challenges & Motivators</h2>
            {getWorkplaceChallengesAndMotivators()}
          </div>
        );
      default:
        return null;
    }
  };

  const onCoachbotClick = async () => {
    dispatch(resetChatbotState());
    dispatch(setIsCoachbotOpen(true));
    const template =
      userAccountId === currentUserAccountId
        ? WORKPLACEINSIGHTS_COACHBOT_PROMPT.workplaceInsightsSelf
        : WORKPLACEINSIGHTS_COACHBOT_PROMPT.workplaceInsightsOther;

    const { userPrompts, fullBotIntro, isStale } = await dispatch(
      getIntroMessageAndPreselectedPrompts({
        type:
          userAccountId === currentUserAccountId
            ? "WorkplaceInsightsReportSelf"
            : "WorkplaceInsightsReportOther",
      })
    ).unwrap();
    if (isStale) return;
    dispatch(
      setCoachBotPrompt({
        userPrompts,
        fullBotIntro,
        chatType: template.chatType ?? "",
        type: "personalityReport",
        report: workplaceInsightsReport,
        chatTypeId: userAccountId,
      })
    );
  };

  const getMoreInsights = () => {
    if (!isMobile && !isPDFBeingDownloaded) {
      return (
        <div className="workplace-insights-more-insights">
          {getMoreInsightsNavBox()}
          {getMoreInsightsShowing()}
        </div>
      );
    }

    const allSections = Object.keys(WORK_PLACE_INSIGHTS_REPORT_SECTION_TITLES);
    return (
      <div className="workplace-insights-more-insights">
        {allSections.map((section, idx) => (
          <React.Fragment key={section}>
            {idx !== 0 && <hr />}
            <div style={{ gridColumn: "span 2" }}>
              {getMoreInsightsShowing(section)}
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const getContent = () => {
    if (workplaceInsightsReport && !newEventId) {
      return (
        <div
          ref={targetRef}
          className={isPDFBeingDownloaded ? "PDF-download-wrapper" : ""}
        >
          <Card className="workplace-insights-report">
            {isSampleUser && (
              <SampleBanner entity="Workplace Insights report" />
            )}

            <div className="workplace-insights-header-row">
              <div className="row-gap-12px">
                {isPDFBeingDownloaded && (
                  <AvatarCircle userAccountId={userAccountId} forPdf={true} />
                )}
                <h2 style={{ marginBottom: "0px" }}>
                  {firstName}'s Workplace Insights
                </h2>
              </div>
              <div className="workplace-insights-header-row__buttons">
                <Button variant="secondary-blue" onClick={() => downloadPDF()}>
                  Download Report
                </Button>
                {currentUserAccountId === userAccountId ? (
                  <Button
                    variant="secondary-blue"
                    onClick={() => setShowRetakeEppModal(true)}
                  >
                    Retake the Personality Profile
                  </Button>
                ) : null}
              </div>
            </div>
            <p>{workplaceInsightsReport?.description}</p>

            {superpowerAndHiddenStrengths?.superPower &&
              getStrengthSection(
                superpowerAndHiddenStrengths?.superPower,
                "Superpower"
              )}
            {superpowerAndHiddenStrengths?.secondHighestTrait &&
              getStrengthSection(
                superpowerAndHiddenStrengths?.secondHighestTrait,
                "Second Highest Trait"
              )}
            {superpowerAndHiddenStrengths?.hiddenStrength &&
              getStrengthSection(
                superpowerAndHiddenStrengths?.hiddenStrength,
                "Hidden Strength"
              )}
            {isPDFBeingDownloaded && (
              <div style={{ height: pageBreakSpacers[0] }} />
            )}
            <div ref={personalitySummaryRef}>
              <SimpleCollapsibleCard
                isOpen={
                  !!openedCards["PersonalitySummary"] || isPDFBeingDownloaded
                }
                setIsOpen={(value: boolean) => {
                  setOpenedCards({
                    ...openedCards,
                    PersonalitySummary: value,
                  });
                }}
                title={
                  currentUserAccountId === userAccountId
                    ? "Your Personality Summary"
                    : `${firstName}'s Personality Summary`
                }
              >
                <div style={{ marginTop: "-20px" }}>{radarChart}</div>
              </SimpleCollapsibleCard>
            </div>

            {isPDFBeingDownloaded && (
              <div style={{ height: pageBreakSpacers[1] }} />
            )}
            <div ref={moreInsightsRef}>
              <SimpleCollapsibleCard
                isOpen={!!openedCards["MoreInsights"] || isPDFBeingDownloaded}
                setIsOpen={(value: boolean) => {
                  setOpenedCards({
                    ...openedCards,
                    MoreInsights: value,
                  });
                }}
                title="More Insights"
              >
                {getMoreInsights()}
              </SimpleCollapsibleCard>
            </div>
          </Card>
          {companySettings?.aiFeaturesEnabled && !isPDFBeingDownloaded ? (
            <CustomChatbotButton
              onCoachbotClick={onCoachbotClick}
              text="Need help understanding this Workplace Insights Report?"
            />
          ) : null}
        </div>
      );
    }

    if (currentUserAccountId !== userAccountId || !pendingTestId) {
      return (
        <NotAvailable
          message={`${firstName} has not yet completed their personality test.`}
        />
      );
    }

    return (
      <PendingTest
        eventId={pendingTestId}
        linkResultsAccess={companySettings?.linkResultsAccess}
        onLinkPreviousResults={() => setShowLinkResultsModal(true)}
      />
    );
  };

  return (
    <>
      <RetakeEppModal
        show={showRetakeEppModal}
        onHide={() => setShowRetakeEppModal(false)}
        onRetake={async () => {
          await dispatch(unlinkTestTaker());
          setShowRetakeEppModal(false);
          dispatch(getPendingTests());
        }}
      />
      <LinkResultsModal
        show={showLinkResultsModal}
        onHide={() => setShowLinkResultsModal(false)}
      />
      {isPDFBeingDownloaded ? <PdfDownloadOverlay /> : null}
      {getContent()}
    </>
  );
}
